<template>
  <standard-dialog
    v-model="model"
    max-width="1200"
    :title="$t('partner_restaurants_settings')"
    :subtitle="company ? `${company.name} | ID: ${company.id}` : ''"
    icon="mdi-handshake"
    :save-text="$t('save')"
    :cancel-text="$t('cancel')"
    :loading="saving"
  >
    <v-row no-gutters class="mt-2">
      <v-col cols="12" md="6" v-if="user.type !== 'restaurant'" class="pr-md-2">
        <v-card outlined class="fill-height">
          <v-card-title class="py-3 grey lighten-4">
            <v-icon left color="primary">mdi-store-search-outline</v-icon>
            {{ $t("all_restaurants") }}
            <v-spacer></v-spacer>
            <v-chip small color="primary" outlined>
              {{ availableRestaurants.length }}
            </v-chip>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-0">
            <text-field
              v-model="searchAvailable"
              :placeholder="$t('search')"
              kind="search"
              clearable
              color="white"
              hide-details
            ></text-field>
            <v-divider></v-divider>
            <div style="height: 500px; overflow-y: auto">
              <v-list-item-group color="primary">
                <v-list-item
                  v-for="(restaurant, i) in filteredAvailableRestaurants"
                  :key="'available-' + restaurant.id"
                  @click="addRestaurant(restaurant)"
                  class="hover-item"
                >
                  <v-list-item-avatar color="primary lighten-4">
                    <v-icon color="primary">mdi-store</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ restaurant.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="restaurant.address">
                      <v-icon small>mdi-map-marker</v-icon>
                      {{ restaurant.address }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn icon small color="primary">
                      <v-icon>mdi-arrow-right-circle</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
                <v-list-item v-if="!filteredAvailableRestaurants.length">
                  <v-list-item-content>
                    <v-alert type="info" text dense class="mb-0">
                      {{
                        searchAvailable
                          ? $t("no_results_found")
                          : $t("no_available_restaurants")
                      }}
                    </v-alert>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        :cols="12"
        :md="user.type !== 'restaurant' ? 6 : 12"
        :class="user.type !== 'restaurant' ? 'pl-md-2' : ''"
      >
        <v-card outlined class="fill-height">
          <v-card-title class="py-3 success lighten-4">
            <v-icon left color="success">mdi-handshake</v-icon>
            {{ $t("partner_restaurants") }}
            <v-spacer></v-spacer>
            <v-chip small color="success" outlined>
              {{ selectedRestaurants.length }}
            </v-chip>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-0">
            <text-field
              v-model="searchSelected"
              :placeholder="$t('search')"
              kind="search"
              clearable
              dense
              hide-details
            ></text-field>
            <v-divider></v-divider>
            <div style="height: 500px; overflow-y: auto">
              <v-list-item-group color="success" v-if="selectedRestaurants.length">
                <v-list-item
                  v-for="(restaurant, i) in filteredSelectedRestaurants"
                  :key="'selected-' + restaurant.id"
                  class="hover-item"
                >
                  <v-list-item-action v-if="user.type !== 'restaurant'">
                    <v-btn icon small color="error" @click="removeRestaurant(restaurant)">
                      <v-icon>mdi-arrow-left-circle</v-icon>
                    </v-btn>
                  </v-list-item-action>
                  <v-list-item-avatar color="success lighten-4">
                    <v-icon color="success">mdi-store-check</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ restaurant.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="restaurant.address">
                      <v-icon small>mdi-map-marker</v-icon>
                      {{ restaurant.address }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="openRestaurantSettings(restaurant)"
                    >
                      <v-icon>mdi-cog</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
                <v-list-item v-if="!filteredSelectedRestaurants.length && searchSelected">
                  <v-list-item-content>
                    <v-alert type="info" text dense class="mb-0">
                      {{ $t("no_results_found") }}
                    </v-alert>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
              <div v-else class="pa-4">
                <v-alert type="info" outlined prominent>
                  <div class="d-flex align-center">
                    <v-icon large class="mr-3">mdi-information-outline</v-icon>
                    <div>
                      <div class="font-weight-bold">
                        {{ $t("no_partner_restaurants") }}
                      </div>
                      <div class="text-caption">
                        {{ $t("please_select_restaurants") }}
                      </div>
                    </div>
                  </div>
                </v-alert>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <template #actions>
      <v-btn text @click="model = false">{{ $t("cancel") }}</v-btn>
      <v-btn color="primary" depressed :loading="saving" @click="save">
        <v-icon left small>mdi-content-save</v-icon>
        {{ $t("save") }}
      </v-btn>
    </template>
  </standard-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import companyService from "@/services/company";
import restaurantService from "@/services/restaurant";
import StandardDialog from "@/components/dialogs/StandardDialog.vue";

export default {
  mixins: [hasModel, hasSnackbar],
  props: {
    company: {
      type: Object,
      default: null,
    },
  },
  components: {
    StandardDialog,
  },
  data: () => ({
    availableRestaurants: [],
    selectedRestaurants: [],
    allRestaurants: [],
    searchAvailable: "",
    searchSelected: "",
    loading: false,
    saving: false,
  }),
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    filteredAvailableRestaurants() {
      if (!this.searchAvailable) return this.availableRestaurants;
      const search = this.searchAvailable.toLowerCase();
      return this.availableRestaurants.filter(
        (r) =>
          r.name.toLowerCase().includes(search) ||
          (r.address && r.address.toLowerCase().includes(search))
      );
    },
    filteredSelectedRestaurants() {
      if (!this.searchSelected) return this.selectedRestaurants;
      const search = this.searchSelected.toLowerCase();
      return this.selectedRestaurants.filter(
        (r) =>
          r.name.toLowerCase().includes(search) ||
          (r.address && r.address.toLowerCase().includes(search))
      );
    },
  },
  watch: {
    model(val) {
      if (val && this.company) {
        this.loadPartners();
      } else {
        // Reset search on close
        this.searchAvailable = "";
        this.searchSelected = "";
      }
    },
    selectedRestaurants(val) {
      if (val && val.length === 1 && this.user.type === "restaurant" && this.model) {
        this.openRestaurantSettings(val[0]);
      }
    },
  },
  created() {
    this.loadAllRestaurants();
  },
  methods: {
    loadAllRestaurants() {
      this.loading = true;
      restaurantService
        .getAllRestaurants()
        .then((res) => {
          const now = new Date();
          const currentTime = now.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });
          this.allRestaurants = res.data.map((i) => {
            i.times = [currentTime];
            return i;
          });
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_loading_restaurants"));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadPartners() {
      if (!this.company?.id) return;

      this.loading = true;
      companyService
        .getCompanyRestaurants(this.company.id)
        .then((res) => {
          const now = new Date();
          const currentTime = now.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });
          this.selectedRestaurants = res.data.map((i) => {
            i.times = i.times || [currentTime];
            return i;
          });

          // Filter out selected restaurants from available list
          const selectedIds = this.selectedRestaurants.map((r) => r.id);
          this.availableRestaurants = this.allRestaurants.filter(
            (r) => !selectedIds.includes(r.id)
          );
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_loading_partners"));
          this.availableRestaurants = [...this.allRestaurants];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    addRestaurant(restaurant) {
      const now = new Date();
      const currentTime = now.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      this.selectedRestaurants.push({
        ...restaurant,
        times: [currentTime],
      });
      // Remove from available list
      const index = this.availableRestaurants.findIndex((r) => r.id === restaurant.id);
      if (index > -1) {
        this.availableRestaurants.splice(index, 1);
      }
    },
    removeRestaurant(restaurant) {
      const index = this.selectedRestaurants.findIndex((r) => r.id === restaurant.id);
      if (index > -1) {
        this.selectedRestaurants.splice(index, 1);
        // Add back to available list
        this.availableRestaurants.unshift(restaurant);
      }
    },
    openRestaurantSettings(restaurant) {
      this.$emit("open-settings", restaurant);
    },
    save() {
      if (!this.selectedRestaurants.length) {
        this.showWarningSnackbar(this.$t("please_select_restaurants"));
        return;
      }

      this.saving = true;
      companyService
        .addRestaurantsToCompany(this.company.id, {
          restaurants: this.selectedRestaurants,
        })
        .then(() => {
          this.showSuccessSnackbar(this.$t("partners_saved"));
          this.$emit("saved");
          this.model = false;
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_saving_partners"));
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.hover-item {
  transition: background-color 0.2s ease;
}

.hover-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
